/**
 * Single blog guide with localized content, FAQ schema, breadcrumbs, related posts.
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo, { SITE_URL } from '../components/Seo';
import {
  getPostBySlug,
  getLocalizedPost,
  getRelatedPosts,
  CATEGORY_LABELS,
} from '../content/blog/posts';
import { APP_HOME, ATS_UPLOAD, REWRITE } from '../utils/paths';
import { isAppPath, localizePath } from '../i18n/localePaths';
import './Blog.css';

const INLINE_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

const toAppOrLocaleHref = (href, lang) => {
  if (!href.startsWith('/')) return href;
  if (isAppPath(href)) return href;
  return localizePath(href, lang);
};

/**
 * Turns `[label](/path)` in blog copy into in-app links.
 *
 * @param {string} text
 * @param {string} keyPrefix
 * @param {'es'|'en'} lang
 * @returns {import('react').ReactNode}
 */
const renderInline = (text, keyPrefix, lang = 'es') => {
  if (!text || typeof text !== 'string' || !text.includes('[')) {
    return text;
  }
  const parts = [];
  let lastIndex = 0;
  INLINE_LINK_RE.lastIndex = 0;
  let match = INLINE_LINK_RE.exec(text);
  while (match) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const label = match[1];
    const href = match[2];
    const key = `${keyPrefix}-${match.index}`;
    if (href.startsWith('/')) {
      parts.push(
        <Link key={key} to={toAppOrLocaleHref(href, lang)}>
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a key={key} href={href} rel="noopener noreferrer">
          {label}
        </a>
      );
    }
    lastIndex = match.index + match[0].length;
    match = INLINE_LINK_RE.exec(text);
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const extractFaq = (sections) => {
  const block = (sections || []).find((s) => s.type === 'faq');
  return block?.items || [];
};

const PROMPT_MARKER = 'Prompt:';
const TIP_PREFIX_RE = /^(Segundo mensaje|Pide después|Ask next|Second message)/i;

/**
 * Splits a paragraph that embeds "Prompt: …" into intro copy and prompt body.
 *
 * @param {string} text
 * @returns {{ intro: string, body: string } | null}
 */
const splitEmbeddedPrompt = (text) => {
  const idx = text.indexOf(PROMPT_MARKER);
  if (idx === -1) {
    return null;
  }
  const intro = text.slice(0, idx).trim();
  const body = text.slice(idx + PROMPT_MARKER.length).trim();
  return body ? { intro, body } : null;
};

/**
 * @param {{ text: string, copiedLabel: string, copyLabel: string, promptLabel: string }} props
 */
const BlogPromptBlock = ({ text, copiedLabel, copyLabel, promptLabel }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="blog-prompt">
      <div className="blog-prompt__head">
        <span className="blog-prompt__label">{promptLabel}</span>
        <button type="button" className="blog-prompt__copy" onClick={handleCopy}>
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
      <p className="blog-prompt__text">{text}</p>
    </div>
  );
};

BlogPromptBlock.propTypes = {
  text: PropTypes.string.isRequired,
  copiedLabel: PropTypes.string.isRequired,
  copyLabel: PropTypes.string.isRequired,
  promptLabel: PropTypes.string.isRequired,
};

/**
 * @param {string} text
 * @param {string} keyPrefix
 * @param {import('react').ReactNode} inline
 * @returns {import('react').ReactNode}
 */
const renderParagraphContent = (text, keyPrefix, lang) => renderInline(text, keyPrefix, lang);

/**
 * @param {object} section
 * @param {number} idx
 * @param {Function} t
 * @param {'es'|'en'} lang
 * @returns {import('react').ReactNode}
 */
const renderParagraphSection = (section, idx, t, lang) => {
  const text = section.text || '';
  const keyPrefix = `p-${idx}`;

  if (TIP_PREFIX_RE.test(text)) {
    return (
      <p key={idx} className="blog-tip">
        {renderParagraphContent(text, keyPrefix, lang)}
      </p>
    );
  }

  const split = splitEmbeddedPrompt(text);
  if (split) {
    return (
      <div key={idx} className="blog-prompt-block">
        {split.intro ? (
          <p className="blog-prompt-block__intro">
            {renderParagraphContent(split.intro, `${keyPrefix}-intro`, lang)}
          </p>
        ) : null}
        <BlogPromptBlock
          text={split.body}
          promptLabel={t('blog.promptLabel')}
          copyLabel={t('blog.promptCopy')}
          copiedLabel={t('blog.promptCopied')}
        />
      </div>
    );
  }

  return <p key={idx}>{renderParagraphContent(text, keyPrefix, lang)}</p>;
};

/**
 * In-article CTA. Default sends to the dashboard. `upload` is the prompts
 * fork (chat vs file). `mid` (or `upload` with custom title/lead) is a
 * contextual card: same layout, copy that matches the article, link to upload.
 */
const BlogArticleCta = ({ variant, t, title, lead, button, from, to, href }) => {
  if (variant === 'upload' || variant === 'mid') {
    const heading = title || t('blog.ctaUploadTitle');
    return (
      <aside className="blog-cta-upload" aria-label={heading}>
        <div className="blog-cta-upload__content">
          <h3 className="blog-cta-upload__title">{heading}</h3>
          <p className="blog-cta-upload__lead">{lead || t('blog.ctaUploadLead')}</p>
          <div className="blog-cta-upload__flow" aria-hidden="true">
            <span className="blog-cta-upload__chip blog-cta-upload__chip--muted">
              {from || t('blog.ctaUploadChatText')}
            </span>
            <span className="blog-cta-upload__flow-line" />
            <span className="blog-cta-upload__chip blog-cta-upload__chip--accent">
              {to || t('blog.ctaUploadFileText')}
            </span>
          </div>
        </div>
        <div className="blog-cta-upload__action">
          <Link
            to={href || (variant === 'mid' ? ATS_UPLOAD : REWRITE)}
            className="btn btn-primary blog-cta-upload__btn"
          >
            {button || t('blog.ctaUploadButton')}
          </Link>
          <p className="blog-cta-upload__fine">{t('blog.ctaUploadFine')}</p>
        </div>
      </aside>
    );
  }

  return (
    <div className="blog-article__cta">
      <p>{t('blog.ctaText')}</p>
      <Link to={APP_HOME} className="btn btn-primary">
        {t('blog.ctaButton')}
      </Link>
    </div>
  );
};

BlogArticleCta.propTypes = {
  variant: PropTypes.string,
  t: PropTypes.func.isRequired,
  title: PropTypes.string,
  lead: PropTypes.string,
  button: PropTypes.string,
  from: PropTypes.string,
  to: PropTypes.string,
  href: PropTypes.string,
};

const BlogPost = () => {
  const { slug } = useParams();
  const { lang, t, localePath } = useLanguage();
  const raw = getPostBySlug(slug);
  const post = raw ? getLocalizedPost(raw, lang) : null;
  const related = useMemo(
    () => (post ? getRelatedPosts(post, lang, 3) : []),
    [post, lang]
  );
  const faqItems = useMemo(() => (post ? extractFaq(post.sections) : []), [post]);
  const categoryLabel = post
    ? (CATEGORY_LABELS[lang === 'en' ? 'en' : 'es'][post.category] || post.category)
    : '';

  if (!post) {
    return (
      <div className="blog-page blog-missing">
        <Seo
          title={t('blog.notFound')}
          description={t('blog.notFound')}
          path={`/blog/${slug || ''}`}
          lang={lang}
          hreflang
          noindex
        />
        <h1>{t('blog.notFound')}</h1>
        <Link to={localePath('/blog')} className="blog-article__back">
          ← {t('blog.back')}
        </Link>
      </div>
    );
  }

  const path = `/blog/${post.slug}`;
  const pageUrl = `${SITE_URL}${localePath(path)}`;

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: lang === 'en' ? 'en' : 'es',
    author: { '@type': 'Organization', name: 'ClarCV' },
    publisher: {
      '@type': 'Organization',
      name: 'ClarCV',
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo512.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    keywords: categoryLabel,
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ClarCV', item: `${SITE_URL}${localePath('/')}` },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('blog.indexTitle'),
        item: `${SITE_URL}${localePath('/blog')}`,
      },
      { '@type': 'ListItem', position: 3, name: post.title, item: pageUrl },
    ],
  };

  const faqLd =
    faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }
      : null;

  const jsonLd = faqLd ? [articleLd, breadcrumbLd, faqLd] : [articleLd, breadcrumbLd];

  return (
    <div className="blog-page">
      <Seo
        title={post.title}
        description={post.description}
        path={path}
        lang={lang}
        hreflang
        type="article"
        keywords={`CV, ATS, ${categoryLabel}, ClarCV`}
        article={{
          publishedTime: post.date,
          modifiedTime: post.date,
          section: categoryLabel,
          tags: [categoryLabel, 'CV', 'ATS'],
        }}
        jsonLd={jsonLd}
      />
      <div className="container">
        <article className="blog-article">
          <nav className="blog-breadcrumbs" aria-label="Breadcrumb">
            <Link to={localePath('/')}>ClarCV</Link>
            <span aria-hidden="true"> / </span>
            <Link to={localePath('/blog')}>{t('nav.blog')}</Link>
            <span aria-hidden="true"> / </span>
            <span>{post.title}</span>
          </nav>
          <Link to={localePath('/blog')} className="blog-article__back">
            ← {t('blog.back')}
          </Link>
          <p className="blog-article__meta">
            <span className="blog-chip">{categoryLabel}</span>
            {post.date} · {post.readMinutes} {t('blog.minRead')}
          </p>
          <h1>{post.title}</h1>
          <p className="blog-article__deck">{post.description}</p>
          {post.sections.map((section, idx) => {
            if (section.type === 'h2') {
              const isPromptHeading = /^Prompt \d+/i.test(section.text);
              return (
                <h2
                  key={idx}
                  className={isPromptHeading ? 'blog-article__h2 blog-article__h2--prompt' : 'blog-article__h2'}
                >
                  {section.text}
                </h2>
              );
            }
            if (section.type === 'ul') {
              return (
                <ul key={idx} className="blog-checklist">
                  {section.items.map((item) => (
                    <li key={item}>{renderInline(item, `ul-${idx}`, lang)}</li>
                  ))}
                </ul>
              );
            }
            if (section.type === 'faq') {
              return (
                <section className="blog-faq" key={idx} aria-labelledby={`faq-heading-${idx}`}>
                  <h2 id={`faq-heading-${idx}`}>{t('blog.faqHeading')}</h2>
                  {section.items.map((item) => (
                    <details key={item.q} className="blog-faq__item">
                      <summary>{item.q}</summary>
                      <p>{renderInline(item.a, `faq-${idx}`, lang)}</p>
                    </details>
                  ))}
                </section>
              );
            }
            if (section.type === 'cta') {
              return (
                <BlogArticleCta
                  key={idx}
                  variant={section.variant}
                  t={t}
                  title={section.title}
                  lead={section.lead}
                  button={section.button}
                  from={section.from}
                  to={section.to}
                  href={section.href}
                />
              );
            }
            return renderParagraphSection(section, idx, t, lang);
          })}
          {related.length > 0 && (
            <aside className="blog-related">
              <h2>{t('blog.related')}</h2>
              <ul>
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link to={localePath(`/blog/${r.slug}`)}>{r.title}</Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
